import { KeyValuePipe, type KeyValue } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { FormField } from '@angular/forms/signals';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PopoverModule } from 'ngx-bootstrap/popover';

import { amountConfig, paymentCurrencies, paymentsList, paymentTypeNames } from '@common/constants/payment-form.const';
import { REG_EXP } from '@common/constants/regexp.const';

import { ELanguages } from '@common/enums';
import { EPaymentType } from '@common/enums/payment.enum';

import { getPaymentMethodCurrency } from '@common/helpers/payment';

import type { PaymentMethod } from '@integrations/payment-methods';
import {
    findPaymentMethodForType,
    isMatchingPaymentType,
    mapAvailablePaymentMethods,
} from '@integrations/payment-methods';

import type { PaymentElementLegacy, RroInfoLegacy } from '@sites/entity-core';

import { SpInputNumComponent } from '../sp-form-controls/sp-input-num/sp-input-num.component';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../ui/sp-switch/src/sp-switch.component';

const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === '';

/**
 * Signal-form twin of `sp-payment-method-settings` (`SpPaymentMethodComponent`).
 * That component owns a real `FormGroup` and reverts it on destroy when dirty —
 * a shape needed by shop-cart, where the FormGroup passed in *is* the live
 * production form.
 *
 * This one binds straight onto the host's signal-form tree: it takes the
 * `FieldTree<PaymentElementLegacy>` node for the element being edited
 * (`form.paymentElementAt(index)`) and reads/writes through it directly — no
 * shadow `model` copy, no `valueChangeEvent` round-trip. Reads go through the
 * element-level `value()` and writes through `value.update()` (rather than
 * per-leaf children) because most `PaymentElementLegacy` fields are optional in
 * the schema, so their FieldTree children only materialise once present — the
 * "missing-key trap". The host owns transaction commit/rollback, so no local
 * dirty-tracking is needed.
 */
@Component({
    selector: 'sp-payment-method-signal',
    templateUrl: './sp-payment-method-signal.component.html',
    styleUrls: ['./sp-payment-method-signal.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        KeyValuePipe,
        TranslateModule,
        BsDropdownModule,
        PopoverModule,
        SpInputNumComponent,
        SpSwitchComponent,
        FormField,
    ],
})
export class SpPaymentMethodSignalComponent {
    /** The signal-form tree node for the element being edited (`form.paymentElementAt(index)`). */
    public readonly field = input.required<FieldTree<PaymentElementLegacy>>();
    public readonly paymentMethods = input.required<PaymentMethod[]>();
    public readonly paymentElements = input.required<PaymentElementLegacy[]>();
    public readonly shopCurrency = input<string>();
    public readonly showRroSettings = input<boolean>(true);

    /** Emits whenever the derived validity changes, so the host can gate its own save button. */
    public readonly isInvalidChangeEvent = output<boolean>();

    private readonly translate = inject(TranslateService);

    public readonly amountConfig = amountConfig;
    public readonly paymentTypeNames = paymentTypeNames;
    public readonly paymentCurrenciesList = paymentCurrencies;

    protected readonly EPaymentType = EPaymentType;

    /** Live value of the bound tree node — the single source of truth for every read below. */
    private readonly element = computed<PaymentElementLegacy | null>(() => this.field()().value() ?? null);

    public readonly availablePaymentMethods = computed(() => mapAvailablePaymentMethods(this.paymentMethods()));

    /** The static payment list minus types already used by sibling elements (custom is always allowed). */
    public readonly paymentsList = computed<typeof paymentsList>(() => {
        const list = structuredClone(paymentsList);
        const currentType = this.type();
        const usedTypes = this.paymentElements()
            .map((element) => element.type)
            .filter((type) => !isMatchingPaymentType(type, currentType));

        for (const key in list) {
            const isUsed = usedTypes.some((usedType) => isMatchingPaymentType(list[key].type, usedType));
            if (isUsed && key !== EPaymentType.custom.toString()) {
                delete list[key];
            }
        }

        return list;
    });

    public readonly type = computed(() => this.element()?.type ?? null);
    public readonly amount = computed(() => this.element()?.amount ?? null);
    public readonly currency = computed(() => this.element()?.currency ?? null);
    public readonly isEditableTotalCost = computed(() => Boolean(this.element()?.isEditableTotalCost));
    public readonly paymentMethodId = computed(() => this.element()?.payment_method_id ?? null);
    public readonly customPaymentName = computed(() => this.element()?.customPaymentName ?? null);
    public readonly rroIsActive = computed(() => Boolean(this.element()?.rro_isActive));
    public readonly rroInfo = computed<RroInfoLegacy | null>(() => this.element()?.rro_info ?? null);
    public readonly rroId = computed(() => this.rroInfo()?.id ?? null);

    public readonly isCustomPaymentType = computed(() => this.type() === EPaymentType.custom);
    public readonly isCryptomusPaymentType = computed(() => this.type() === EPaymentType.cryptomus);

    /** The other payment method configured for the same type (e.g. Stripe vs Stripe Connect). */
    public readonly paymentMethodByType = computed<PaymentMethod | null>(() => {
        const currentElement = this.element();
        if (!currentElement) return null;
        if (currentElement.type === EPaymentType.custom) return currentElement as unknown as PaymentMethod;

        return this.paymentMethods().find((payment) => payment.id === currentElement.payment_method_id) ?? null;
    });

    public readonly paymentMethodsByType = computed<PaymentMethod[]>(() => {
        const currentType = Number(this.type());

        return this.paymentMethods().filter((payment) => isMatchingPaymentType(payment.type, currentType));
    });

    public readonly isAmountInvalid = computed(() => isEmpty(this.amount()) || Number(this.amount()) < 0.01);
    public readonly isCustomPaymentNameInvalid = computed(
        () => this.isCustomPaymentType() && isEmpty(this.customPaymentName())
    );
    public readonly isRroIdInvalid = computed(() => {
        if (!this.rroIsActive()) return false;

        const id = this.rroId();
        if (isEmpty(id)) return true;

        return this.type() === EPaymentType.liqpay ? !REG_EXP.NUMBER.test(String(id)) : false;
    });

    public readonly isInvalid = computed(
        () =>
            !this.availablePaymentMethods()?.[this.type()] ||
            this.isAmountInvalid() ||
            this.isRroIdInvalid() ||
            this.isCustomPaymentNameInvalid()
    );

    constructor() {
        // `backfillDefaultPaymentName` patches the bound tree node, which
        // re-runs this effect. A one-shot guard (same idiom as
        // `WebPaymentMethodsSignalCollectionComponent`'s seeding effect) keeps
        // that write-back from firing more than once and looping forever.
        let backfilled = false;

        effect(() => {
            const current = this.element();
            if (!current) return;

            if (!backfilled) {
                backfilled = true;
                this.backfillDefaultPaymentName(current);
            }
        });

        effect(() => {
            this.isInvalidChangeEvent.emit(this.isInvalid());
        });
    }

    private backfillDefaultPaymentName(element: PaymentElementLegacy): void {
        if (element.newPaymentName) return;

        const label =
            element.type !== EPaymentType.custom
                ? this.translate.instant(paymentsList[element.type]?.label)
                : this.translate.instant('custom_payment_label');

        this.patch({ newPaymentName: label });
    }

    public paymentIndexOrder = (akv: KeyValue<string, any>, bkv: KeyValue<string, any>): number => {
        const a = akv.value.index;
        const b = bkv.value.index;

        return a > b ? 1 : b > a ? -1 : 0;
    };

    public setType(type: EPaymentType): void {
        const isCustom = type === EPaymentType.custom;
        const matchedMethod = isCustom ? null : findPaymentMethodForType(this.paymentMethods(), type);
        const resolvedType = (matchedMethod?.type as EPaymentType) ?? type;

        const userCurrency = this.shopCurrency();
        const currency =
            isCustom && userCurrency ? userCurrency : getPaymentMethodCurrency(matchedMethod?.settings) || 'USD';

        this.patch({
            type: resolvedType,
            currency,
            payment_method_id: isCustom ? 'custom_payment_id' : (matchedMethod?.id ?? null),
            isEditableTotalCost: false,
            amount: 1,
            customPaymentName: this.translate.instant('custom_payment_label'),
            newPaymentName: isCustom
                ? this.translate.instant('custom_payment_label')
                : this.translate.instant(paymentsList[resolvedType]?.label),
            rro_isActive: false,
            rro_info: { ...this.rroInfo(), id: null },
        });
    }

    public setPaymentMethodId(paymentMethod: PaymentMethod): void {
        const patch: Partial<PaymentElementLegacy> = {
            currency: getPaymentMethodCurrency(paymentMethod.settings),
            payment_method_id: paymentMethod.id,
        };

        if (Number(this.type()) === EPaymentType.stripeConnect && paymentMethod.type === EPaymentType.stripe) {
            patch.type = EPaymentType.stripe;
        }
        if (Number(this.type()) === EPaymentType.stripe && paymentMethod.type === EPaymentType.stripeConnect) {
            patch.type = EPaymentType.stripeConnect;
        }

        this.patch(patch);
    }

    public setRroIsActive(value: boolean): void {
        const patch: Partial<PaymentElementLegacy> = { rro_isActive: value };

        if (value) {
            const amount = this.amount();
            patch.rro_info = { ...this.rroInfo(), amount: 1, cost: amount, price: amount };
        }

        this.patch(patch);
    }

    public setRroId(event: Event): void {
        this.patch({ rro_info: { ...this.rroInfo(), id: (event.target as HTMLInputElement).value } });
    }

    public setAmount(value: number): void {
        this.patch({ amount: value });
    }

    public setCurrency(value: string): void {
        this.patch({ currency: value });
    }

    public onIsEditableTotalCostChange(event: Event): void {
        this.patch({ isEditableTotalCost: (event.target as HTMLSelectElement).value === 'true' });
    }

    public getPaymentIconSrc(type: PaymentMethod['type']): string {
        return `./assets/img/websites/payment-icons/sm/${paymentTypeNames[type]}-sm.svg`;
    }

    public get rroLiqHelpLink(): string {
        const lang = this.translate.currentLang as ELanguages;

        switch (lang) {
            case ELanguages.RU:
                return 'https://sendpulse.com/ru/knowledge-base/landing-page/website-builder/rro-liqpay';
            case ELanguages.UA:
                return 'https://sendpulse.com/ua/knowledge-base/landing-page/website-builder/rro-liqpay';
            default:
                return 'https://sendpulse.com/knowledge-base/landing-page/website-builder/rro-liqpay';
        }
    }

    public get rroMonoHelpLink(): string {
        const lang = this.translate.currentLang as ELanguages;

        switch (lang) {
            case ELanguages.RU:
                return 'https://sendpulse.com/ru/knowledge-base/landing-page/website-builder/rro-monobank';
            case ELanguages.UA:
                return 'https://sendpulse.com/ua/knowledge-base/landing-page/website-builder/rro-monobank';
            default:
                return 'https://sendpulse.com/knowledge-base/landing-page/website-builder/rro-monobank';
        }
    }

    /**
     * Writes through the element-level tree node (never per-leaf children) so
     * currently-absent optional keys are materialised rather than tripping the
     * missing-key trap.
     */
    private patch(patch: Partial<PaymentElementLegacy>): void {
        this.field()().value.update((element) => ({ ...element, ...patch }));
    }
}
